import { barrierLabel, barrierPercent } from "../lib/barrierCopy";
import { PRESETS } from "../lib/presets";
import type { LoopState } from "../state/roundLoop";
import { useSettingsStore } from "../state/settingsStore";
import type { BinaryLabel, Preset, PresetResolution, Round } from "../types";
import { BarrierRule } from "./BarrierRule";
import type { OverlayLine } from "./Chart";
import { GuessControls } from "./GuessControls";
import { HistoryPanels, HistoryToggles } from "./HistoryPanel";
import { LoopShell } from "./LoopShell";
import { NoteField } from "./NoteField";
import { ResultBanner } from "./ResultBanner";

/**
 * The Predict trend Loop (Higher/Lower) over a barrier bank (spec §7): does
 * price reach the upper or the lower barrier first. The Round lifecycle and
 * page chrome come from LoopShell; this view supplies the Context slicing,
 * the barrier overlays, and the footer (GuessControls / ResultBanner).
 *
 * GRA-122: the barrier lines are labeled and drawn from the moment a Round
 * loads, not only at reveal, so the player sees what they're betting on
 * before committing. This change moved the sentence that used to state the
 * mechanics above the guess buttons into BarrierRule, which asks the question
 * first and keeps the ATR arithmetic behind a help control.
 *
 * GRA-124: at reveal, a "Decision" vertical marker pins down what the
 * barriers were measured from: the last Context candle, the same `entry`
 * the barriers and `resolution.pctMove` are struck from. Where the round
 * ended (whichever barrier it was, or the time cap) is where the chart's own
 * reveal step already opens, so it doesn't need a marker of its own.
 */

/** What a trend Loop's store must look like from here (see ADR-0008). */
export type TrendStore = <T>(
  selector: (state: LoopState<Round, BinaryLabel, { resolution: PresetResolution }>) => T,
) => T;

export type TrendViewProps = {
  store: TrendStore;
  loadingMessage: string;
  /**
   * Play parameters fixed by the Loop rather than chosen by the player (the
   * Daily challenge, GRA-139: a shared puzzle has to be the same chart and
   * the same resolution for everybody). Omitted, the player's own settings
   * decide, which is what free play does.
   */
  fixed?: { preset: Preset; contextLength: number };
  /** Passed through to LoopShell; "none" drops the market row (the challenge). */
  markets?: "from-bank" | "none";
  /**
   * Whether to render the history (l) and session (s) toggles. Defaults to
   * true; the Daily challenge passes false because a run already has its own
   * progress readout and result card, and the session summary mixes free and
   * challenge rounds and carries reset/share controls that do not belong
   * mid-run.
   */
  history?: boolean;
};

export function TrendView({
  store,
  loadingMessage,
  fixed,
  markets = "from-bank",
  history = true,
}: TrendViewProps) {
  const settingsContextLength = useSettingsStore((s) => s.contextLength);
  const settingsPreset = useSettingsStore((s) => s.preset);
  const contextLength = fixed?.contextLength ?? settingsContextLength;
  const preset = fixed?.preset ?? settingsPreset;
  const difficulty = PRESETS[preset];
  const { nMax } = difficulty;
  const current = store((s) => s.current);

  // The Round's precomputed per-preset barriers (entry ± m·ATR), available
  // from the moment a Round loads, not only at reveal (GRA-122): a played
  // Round always has a resolution for every preset (ADR-0004). `entry` is the
  // last Context candle's close the barriers were struck from, same one
  // `labeling.ts` uses, regardless of the Context-length slice shown.
  const barrierOverlays: OverlayLine[] = (() => {
    if (!current) return [];
    const resolution = current.resolutions[preset];
    const entry = current.context[current.context.length - 1].close;
    return [
      {
        value: resolution.upperBarrier,
        tone: "up",
        label: barrierLabel("upper", resolution.upperBarrier, entry),
        compactLabel: barrierPercent(resolution.upperBarrier, entry),
      },
      {
        value: resolution.lowerBarrier,
        tone: "down",
        label: barrierLabel("lower", resolution.lowerBarrier, entry),
        compactLabel: barrierPercent(resolution.lowerBarrier, entry),
      },
    ];
  })();

  // The reveal marker (GRA-124): "Decision" at the last Context candle
  // charted (the entry point above, wherever the Context-length slice puts
  // it). Its position is a dataIndex into the plotted series (Context, then
  // Forward), so the marker doesn't carry a price and stays as anonymized as
  // the rest of the chart. Shown only once revealed: before that the
  // resolution candle isn't charted at all.
  const revealMarkers = (
    reveal: { resolution: PresetResolution } | null,
    revealed: boolean,
  ): OverlayLine[] => {
    if (!revealed || !reveal || !current) return [];
    const decisionIndex = Math.min(contextLength, current.context.length) - 1;
    return [{ dataIndex: decisionIndex, tone: "muted", label: "Decision", anchor: "top" }];
  };

  return (
    <LoopShell
      store={store}
      loadingMessage={loadingMessage}
      markets={markets}
      chartContext={(round) => round.context.slice(-contextLength)}
      chartForward={(round) => round.forward.slice(0, nMax)}
      overlays={({ reveal, revealed }) => [...barrierOverlays, ...revealMarkers(reveal, revealed)]}
      // The reveal opens on the resolution candle (GRA-147), the same
      // `daysToResolve` the "Resolved" marker above already points at,
      // rather than walking the whole barrier path first.
      resolutionStep={({ reveal }) => reveal?.resolution.daysToResolve ?? 0}
      historyToggles={history ? <HistoryToggles /> : undefined}
      historyPanels={history ? <HistoryPanels /> : undefined}
      footer={({ round, commit, reveal, revealed, submit, next, note = "", setNote = () => {} }) =>
        revealed && commit && reveal ? (
          <ResultBanner
            guess={commit}
            resolution={reveal.resolution}
            entry={round.context[round.context.length - 1].close}
            forward={round.forward}
            note={note}
            setup={round.setup}
            onNext={next}
          />
        ) : (
          <div className="flex flex-col items-center gap-3">
            <BarrierRule difficulty={difficulty} />
            <GuessControls onGuess={submit} disabled={revealed} />
            <NoteField value={note} onChange={setNote} disabled={revealed} />
          </div>
        )
      }
    />
  );
}
